import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { injectDispatch, provideDispatcher } from '@ngrx/signals/events';
import { TranslateModule } from '@ngx-translate/core';
import { AlertMessageModule, SkeletonComponent, WuiDialog } from 'wflow-ui';
import { selectCurrentOrganizationIdentity } from 'wflow-main-app/src/app/store/identity/identity.selectors';
import { toPeppolDetailEvent } from '../../adapters/peppol-message-base.adapter';
import {
  PeppolAttachment,
  PeppolDetailEvent,
} from '../../interfaces/peppol-detail.interface';
import { PeppolDirection } from '../../interfaces/peppol-item.dto';
import { PeppolUblPreview } from '../../interfaces/peppol-ubl-preview.interface';
import {
  acceptsStatusActions,
  peppolStatusActions,
  visiblePeppolActions,
} from '../../const/peppol-actions.const';
import { PeppolStatusDialogService } from '../../services/peppol-status-dialog.service';
import {
  PeppolAction,
  PeppolActionConfig,
} from '../../interfaces/peppol-action.interface';
import { PEPPOL_XML_CONTENT_TYPE } from './peppol-detail-dialog.const';
import { PeppolDetailDocumentRowComponent } from './sections/peppol-detail-document-row/peppol-detail-document-row.component';
import { PeppolDetailFilesComponent } from './sections/peppol-detail-files/peppol-detail-files.component';
import { PeppolDetailPreviewComponent } from './sections/peppol-detail-preview/peppol-detail-preview.component';
import { PeppolDetailRawXmlComponent } from './sections/peppol-detail-raw-xml/peppol-detail-raw-xml.component';
import { PeppolDetailSbdhComponent } from './sections/peppol-detail-sbdh/peppol-detail-sbdh.component';
import { PeppolDetailStatusComponent } from './sections/peppol-detail-status/peppol-detail-status.component';
import { peppolDetailPageEvents } from './store/peppol-detail.events';
import { PeppolDetailStore } from './store/peppol-detail.store';

@Component({
  selector: 'wf-peppol-detail-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    AlertMessageModule,
    SkeletonComponent,
    PeppolDetailDocumentRowComponent,
    PeppolDetailFilesComponent,
    PeppolDetailPreviewComponent,
    PeppolDetailRawXmlComponent,
    PeppolDetailSbdhComponent,
    PeppolDetailStatusComponent,
  ],
  providers: [
    provideDispatcher(),
    PeppolDetailStore,
    PeppolStatusDialogService,
  ],
  templateUrl: './peppol-detail-dialog.component.html',
  styleUrl: './peppol-detail-dialog.component.scss',
})
export class PeppolDetailDialogComponent implements OnInit {
  protected readonly store = inject(PeppolDetailStore);
  private readonly dispatch = injectDispatch(peppolDetailPageEvents);
  private readonly ngrxStore = inject(Store);
  private readonly dialogRef = inject(WuiDialog);
  private readonly statusDialogSvc = inject(PeppolStatusDialogService);

  readonly messageId = input.required<string>();
  readonly hideDocumentLink = input(false);
  readonly canReject = input(false);

  private readonly identity = toSignal(
    this.ngrxStore.select(selectCurrentOrganizationIdentity),
  );

  protected readonly statusEvent = computed<PeppolDetailEvent | null>(() => {
    const message = this.store.message();
    return message ? toPeppolDetailEvent(message) : null;
  });

  protected readonly statusActions = computed<PeppolActionConfig[]>(() => {
    const message = this.store.message();
    return acceptsStatusActions(message) && !this.store.sendingStatus()
      ? visiblePeppolActions(peppolStatusActions(this.canReject()), message)
      : [];
  });

  protected readonly loadErrorText = computed(() =>
    this.toLoadErrorText(this.store.loadError()),
  );

  protected readonly xmlAttachment = computed<PeppolAttachment | null>(() =>
    this.findXmlAttachment(this.store.attachments()),
  );

  protected readonly documentPreview = computed<PeppolUblPreview | null>(() =>
    this.withProfileAddress(
      this.store.ublPreview(),
      this.store.detail()?.direction,
      this.identity()?.address,
    ),
  );

  ngOnInit(): void {
    this.dispatch.initialized({ messageId: this.messageId() });
  }

  private toLoadErrorText(
    error: HttpErrorResponse | Error | null,
  ): { key: string; code?: number } {
    if (!(error instanceof HttpErrorResponse) || !error.status) {
      return { key: 'PEPPOL.Detail.LoadError' };
    }
    if (error.status === 404) {
      return { key: 'NotFound404' };
    }
    return {
      key: 'BasicErrorHandlerService_ErrorTextWithStatus',
      code: error.status,
    };
  }

  private withProfileAddress(
    preview: PeppolUblPreview | null,
    direction: PeppolDirection | undefined,
    address: string | undefined,
  ): PeppolUblPreview | null {
    if (!preview || !address) {
      return preview;
    }
    if (direction === PeppolDirection.Outgoing) {
      return { ...preview, sender: { ...preview.sender, address } };
    }
    return { ...preview, receiver: { ...preview.receiver, address } };
  }

  private findXmlAttachment(
    attachments: readonly PeppolAttachment[],
  ): PeppolAttachment | null {
    return (
      attachments.find(
        (attachment) => attachment.contentType === PEPPOL_XML_CONTENT_TYPE,
      ) ?? null
    );
  }

  protected onViewFile(attachment: PeppolAttachment): void {
    this.dispatch.fileViewed({ name: attachment.name });
    if (attachment.contentType !== PEPPOL_XML_CONTENT_TYPE) {
      this.dispatch.attachmentRequested({ name: attachment.name });
    }
  }

  protected onDownloadFile(attachment: PeppolAttachment): void {
    this.dispatch.downloadRequested({ name: attachment.name });
  }

  protected onShowRawXml(): void {
    const xml = this.xmlAttachment();
    if (xml) {
      this.dispatch.attachmentRequested({ name: xml.name });
    }
  }

  protected onCreateDocument(): void {
    this.dialogRef.close({ createDocument: true });
  }

  protected onStatusAction(action: PeppolAction): void {
    if (action === PeppolAction.Reject) {
      this.openRejectDialog();
      return;
    }

    this.confirmStatusAction(action);
  }

  private confirmStatusAction(action: PeppolAction): void {
    this.statusDialogSvc
      .confirm(action)
      .subscribe(() => this.dispatch.statusActionConfirmed({ action }));
  }

  private openRejectDialog(): void {
    this.statusDialogSvc
      .collectRejection()
      .subscribe((request) => this.dispatch.rejectConfirmed({ request }));
  }
}
